import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

const initialForm = {
  payrollPeriodId: "",
  dueDate: "",
  installmentAmount: "",
  paidAmount: 0,
  balanceAmount: "",
  remarks: "",
};

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
`;

function Field({ label, children }) {
  return (
    <div className="relative">
      <label className={labelClass}>
        {label}
      </label>

      {children}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  readOnly = false,
  error = false,
  helperText,
}) {
  return (
    <div>
      <Field label={label}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={`
            ${inputClass}
            ${readOnly ? "bg-surface-50" : ""}
            ${
              error
                ? "border-[#C0392B] focus:border-[#C0392B] focus:ring-[#C0392B]"
                : ""
            }
          `}
        />
      </Field>

      {helperText && (
        <p className="mt-1 px-1 text-[12px] text-[#C0392B]">
          {helperText}
        </p>
      )}
    </div>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
}) {
  return (
    <Field label={label}>
      <select
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-auto cursor-pointer`}
      >
        {children}
      </select>
    </Field>
  );
}

function DateInput({
  label,
  value,
  onChange,
  error = false,
  helperText,
}) {
  return (
    <div>
      <Field label={label}>
        <input
          type="date"
          value={value}
          onChange={onChange}
          className={`
            ${inputClass}
            appearance-none
            ${
              error
                ? "border-[#C0392B] focus:border-[#C0392B] focus:ring-[#C0392B]"
                : ""
            }
          `}
        />
      </Field>

      {helperText && (
        <p className="mt-1 px-1 text-[12px] text-[#C0392B]">
          {helperText}
        </p>
      )}
    </div>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  rows = 2,
}) {
  return (
    <Field label={label}>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        className="
          w-full
          rounded-md
          border border-surface-200
          bg-white
          px-3.5
          py-3
          text-[16px]
          text-surface-800
          outline-none
          transition-colors
          focus:border-primary-500
          focus:ring-1
          focus:ring-primary-500
        "
      />
    </Field>
  );
}

export default function AddLoanInstallmentDialog({
  open,
  onClose,
  onSave,
  loading = false,
  loan,
  payrollPeriods = [],
  existingInstallments = [],
}) {
  const [form, setForm] = useState(initialForm);

  const [errors, setErrors] = useState({
    dueDate: "",
    paidAmount: "",
  });

  useEffect(() => {
    if (!open || !loan) return;

    setForm({
      payrollPeriodId: loan.HLM_START_PERIOD_ID ?? "",
      dueDate: "",
      installmentAmount: Number(
        loan.HLM_INSTALLMENT_AMOUNT || 0
      ),
      paidAmount: 0,
      balanceAmount: Number(
        loan.HLM_INSTALLMENT_AMOUNT || 0
      ),
      remarks: "",
    });

    setErrors({
      dueDate: "",
      paidAmount: "",
    });
  }, [open, loan]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (
        field === "installmentAmount" ||
        field === "paidAmount"
      ) {
        const installment = Number(
          field === "installmentAmount"
            ? value
            : next.installmentAmount
        );

        const paid = Number(
          field === "paidAmount"
            ? value
            : next.paidAmount
        );

        next.balanceAmount = Math.max(
          installment - paid,
          0
        );
      }

      return next;
    });

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.dueDate) {
      newErrors.dueDate = "Due date is required.";
    }

    if (
      Number(form.paidAmount) >
      Number(form.installmentAmount)
    ) {
      newErrors.paidAmount =
        "Paid amount cannot be greater than the installment amount.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      loanId: Number(loan.HLM_LOAN_ID),

      installmentNo:
        existingInstallments.length + 1,

      payrollPeriodId:
        Number(form.payrollPeriodId),

      dueDate: form.dueDate,

      installmentAmount:
        Number(form.installmentAmount),

      paidAmount:
        Number(form.paidAmount),

      balanceAmount:
        Number(form.balanceAmount),

      status: 1,

      // TODO:
      // Replace once payroll header integration is available.
      payrollId: 0,

      remarks: form.remarks,
    });
  };

  const installmentNo =
    existingInstallments.length + 1;

  const installmentAmount =
    Number(form.installmentAmount || 0);

  const paidAmount =
    Number(form.paidAmount || 0);

  const balanceAmount =
    Number(form.balanceAmount || 0);

  const paidExceedsInstallment =
    paidAmount > installmentAmount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          Add Loan Installment
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4">

          {/* Loan Number */}
          <TextInput
            label="Loan Number"
            value={loan?.HLM_LOAN_NO ?? ""}
            readOnly
          />

          {/* Installment Number */}
          <TextInput
            label="Installment No"
            value={installmentNo}
            readOnly
          />

          {/* Payroll Period */}
          <SelectInput
            label="Payroll Period"
            value={form.payrollPeriodId}
            onChange={handleChange("payrollPeriodId")}
          >
            <option value="">
              Select Payroll Period
            </option>

            {payrollPeriods.map((period) => (
              <option
                key={period.id}
                value={period.id}
              >
                {period.code} - {period.name}
              </option>
            ))}
          </SelectInput>

          {/* Due Date */}
          <DateInput
            label="Due Date"
            value={form.dueDate}
            onChange={handleChange("dueDate")}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
          />

          {/* Installment Amount */}
          <TextInput
            label="Installment Amount"
            type="number"
            value={form.installmentAmount}
            onChange={handleChange("installmentAmount")}
          />

          {/* Paid Amount */}
          <TextInput
            label="Paid Amount"
            type="number"
            value={form.paidAmount}
            onChange={handleChange("paidAmount")}
            error={
              !!errors.paidAmount ||
              paidExceedsInstallment
            }
            helperText={
              errors.paidAmount ||
              (paidExceedsInstallment
                ? "Paid amount cannot be greater than the installment amount."
                : "")
            }
          />

          {/* Balance Amount */}
          <TextInput
            label="Balance Amount"
            type="number"
            value={form.balanceAmount}
            readOnly
            helperText="Installment Amount − Paid Amount"
          />

          {/* Remarks */}
          <TextAreaInput
            label="Remarks"
            value={form.remarks}
            onChange={handleChange("remarks")}
            rows={3}
          />

          {/* Summary */}
          <div className="rounded-lg bg-[#F0F7F0] p-3">
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div>
                <p className="text-surface-500">
                  Installment
                </p>
                <p className="font-medium text-surface-800">
                  #{installmentNo}
                </p>
              </div>

              <div>
                <p className="text-surface-500">
                  Installment Amount
                </p>
                <p className="font-medium text-surface-800">
                  {installmentAmount.toFixed(2)}
                </p>
              </div>

              <div>
                <p className="text-surface-500">
                  Paid Amount
                </p>
                <p className="font-medium text-surface-800">
                  {paidAmount.toFixed(2)}
                </p>
              </div>

              <div>
                <p className="text-surface-500">
                  Balance
                </p>
                <p className="font-medium text-primary-700">
                  {balanceAmount.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Validation Warning */}
          {paidExceedsInstallment && (
            <div className="rounded-lg bg-[#FDECEC] p-3">
              <p className="text-[12px] text-[#C0392B]">
                The paid amount cannot exceed the
                installment amount. Please adjust the
                amount before saving.
              </p>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          className="!text-primary-600"
        >
          CANCEL
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            loading ||
            !form.payrollPeriodId ||
            !form.dueDate ||
            paidExceedsInstallment
          }
          className="!bg-primary-600 hover:!bg-primary-700"
        >
          {loading ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
